/**
 * Permission Definitions
 * 
 * Centralized definition of all permissions in the system.
 * Permissions are granular, string-based constants that represent
 * specific actions or access rights.
 * 
 * Naming Convention: RESOURCE_ACTION (e.g., USER_CREATE, GAME_VIEW)
 */

// User Management Permissions
export const USER_CREATE = 'USER_CREATE';
export const USER_EDIT = 'USER_EDIT';
export const USER_DELETE = 'USER_DELETE';
export const USER_VIEW = 'USER_VIEW';
export const USER_LIST = 'USER_LIST';

// Game Management Permissions
export const GAME_CREATE = 'GAME_CREATE';
export const GAME_EDIT = 'GAME_EDIT';
export const GAME_DELETE = 'GAME_DELETE';
export const GAME_VIEW = 'GAME_VIEW';
export const GAME_LIST = 'GAME_LIST';
export const GAME_OPEN = 'GAME_OPEN';

// Soda Management Permissions
export const SODA_CREATE = 'SODA_CREATE';
export const SODA_EDIT = 'SODA_EDIT';
export const SODA_DELETE = 'SODA_DELETE';
export const SODA_VIEW = 'SODA_VIEW';
export const SODA_VIEW_ALL = 'SODA_VIEW_ALL';
export const SODA_VIEW_PARTNER_ONLY = 'SODA_VIEW_PARTNER_ONLY';

// Party Management Permissions
export const PARTY_CREATE = 'PARTY_CREATE';
export const PARTY_EDIT = 'PARTY_EDIT';
export const PARTY_DELETE = 'PARTY_DELETE';
export const PARTY_VIEW = 'PARTY_VIEW';
export const PARTY_LIST = 'PARTY_LIST';

// Report Permissions
export const REPORT_VIEW = 'REPORT_VIEW';
export const REPORT_LEDGER = 'REPORT_LEDGER';
export const REPORT_TRIAL_BALANCE = 'REPORT_TRIAL_BALANCE';
export const REPORT_HAVALA = 'REPORT_HAVALA';
export const REPORT_MATCH = 'REPORT_MATCH';

// Havala actions. Viewing the havala pages uses REPORT_HAVALA.
export const HAVLA_CREATE = 'HAVLA_CREATE';
export const HAVLA_EDIT = 'HAVLA_EDIT';
export const HAVLA_DELETE = 'HAVLA_DELETE';

// Utility actions. Kept separate so a role can add parties without seeing every utility tool.
export const MATCH_RECALL = 'MATCH_RECALL';
export const IMPORT_ACCOUNT = 'IMPORT_ACCOUNT';
export const DATABASE_BACKUP = 'DATABASE_BACKUP';
export const QUICK_ADD_PARTY = 'QUICK_ADD_PARTY';

// Partner Specific Permissions
export const PARTNER_SUMMARY_VIEW = 'PARTNER_SUMMARY_VIEW';
export const PARTNER_TRANSACTIONS_VIEW = 'PARTNER_TRANSACTIONS_VIEW';

/**
 * Type-safe permission string union
 */
export type Permission = 
  | typeof USER_CREATE
  | typeof USER_EDIT
  | typeof USER_DELETE
  | typeof USER_VIEW
  | typeof USER_LIST
  | typeof GAME_CREATE
  | typeof GAME_EDIT
  | typeof GAME_DELETE
  | typeof GAME_VIEW
  | typeof GAME_LIST
  | typeof GAME_OPEN
  | typeof SODA_CREATE
  | typeof SODA_EDIT
  | typeof SODA_DELETE
  | typeof SODA_VIEW
  | typeof SODA_VIEW_ALL
  | typeof SODA_VIEW_PARTNER_ONLY
  | typeof PARTY_CREATE
  | typeof PARTY_EDIT
  | typeof PARTY_DELETE
  | typeof PARTY_VIEW
  | typeof PARTY_LIST
  | typeof REPORT_VIEW
  | typeof REPORT_LEDGER
  | typeof REPORT_TRIAL_BALANCE
  | typeof REPORT_HAVALA
  | typeof REPORT_MATCH
  | typeof HAVLA_CREATE
  | typeof HAVLA_EDIT
  | typeof HAVLA_DELETE
  | typeof MATCH_RECALL
  | typeof IMPORT_ACCOUNT
  | typeof DATABASE_BACKUP
  | typeof QUICK_ADD_PARTY
  | typeof PARTNER_SUMMARY_VIEW
  | typeof PARTNER_TRANSACTIONS_VIEW;

/**
 * Array of all available permissions
 */
export const ALL_PERMISSIONS: Permission[] = [
  USER_CREATE,
  USER_EDIT,
  USER_DELETE,
  USER_VIEW,
  USER_LIST,
  GAME_CREATE,
  GAME_EDIT,
  GAME_DELETE,
  GAME_VIEW,
  GAME_LIST,
  GAME_OPEN,
  SODA_CREATE,
  SODA_EDIT,
  SODA_DELETE,
  SODA_VIEW,
  SODA_VIEW_ALL,
  SODA_VIEW_PARTNER_ONLY,
  PARTY_CREATE,
  PARTY_EDIT,
  PARTY_DELETE,
  PARTY_VIEW,
  PARTY_LIST,
  REPORT_VIEW,
  REPORT_LEDGER,
  REPORT_TRIAL_BALANCE,
  REPORT_HAVALA,
  REPORT_MATCH,
  HAVLA_CREATE,
  HAVLA_EDIT,
  HAVLA_DELETE,
  MATCH_RECALL,
  IMPORT_ACCOUNT,
  DATABASE_BACKUP,
  QUICK_ADD_PARTY,
  PARTNER_SUMMARY_VIEW,
  PARTNER_TRANSACTIONS_VIEW,
];

/**
 * Check if a string is a valid permission
 */
export const isValidPermission = (permission: string): permission is Permission => {
  return ALL_PERMISSIONS.includes(permission as Permission);
};

// Ensure this file is treated as a module
export {};

